import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { fadeIn } from "../variants";

import { getExperiences, getEducations } from "./api";

import "../components/Experiences.css"; // 确保你有这个样式文件

import { GrLocationPin } from "react-icons/gr";

const Experience = ({ onToggle, isDarkMode }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [scrollDirection, setScrollDirection] = useState("up");
  const [lastScrollY, setLastScrollY] = useState(0);
  const [visible, setVisible] = useState(true);

  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);

  const [pushed, setPushed] = useState(false);
  const experienceSectionRef = useRef(null);

  useEffect(() => {
    getExperiences().then(setExperiences);
    getEducations().then(setEducations);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        setScrollDirection("down");
      } else if (currentScrollY < lastScrollY) {
        setScrollDirection("up");
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
      },
      { threshold: 0.4 }
    );

    const section = document.getElementById("experience");
    if (section) {
      observer.observe(section);
      experienceSectionRef.current = section;
    }

    return () => {
      if (experienceSectionRef.current) {
        observer.unobserve(experienceSectionRef.current);
      }
    };
  }, []);

  return (
    <section className="py-8" id="experience">
      <div className="container mx-auto px-4 pb-20">
        <div className="flex flex-col items-center justify-center">
          <div
            className="mb-8 cursor-pointer relative h-[200px] flex items-center justify-center"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onToggle}
          >
            <AnimatePresence mode="wait">
              {!isHovered ? (
                <motion.div
                  key="experience"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute"
                >
                  <p className="font-bold text-6xl lg:text-9xl">Experience</p>
                  <p className="text-xl lg:text-3xl pl-2 text-center">
                    View my journey
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="skills"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute flex items-center gap-4"
                >
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ChevronLeft className="w-20 h-20 text-rose-600 dark:text-green-500" />
                  </motion.div>
                  <div>
                    <p className="font-bold text-6xl lg:text-9xl text-rose-600 dark:text-green-500 text-center">
                      Skills
                    </p>
                    <p className="text-xl lg:text-3xl pl-2 text-rose-600 dark:text-green-500">
                      What skills do I have?
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {visible && (
            <button
              className="fixed top-1/2 -translate-y-1/2 text-[40px] bg-transparent font-bold dark:text-green-600 text-rose-600 border-none"
              onClick={() => setPushed((prev) => !prev)}
              style={{
                left: pushed ? undefined : "10rem",
                right: pushed ? "10rem" : undefined,
              }}
            >
              <span
                className={`inline-block writing-mode-vertical-lr text-orientation-upright ${
                  pushed
                    ? "vertical-up"
                    : "rotate-180 origin-center vertical-down"
                }`}
              >
                {pushed ? "Show Experience" : "Show Education"}
              </span>
            </button>
          )}

          {/* 主要内容容器 */}
          <motion.div
            className="relative w-full p-4 overflow-hidden"
            animate={{
              height: pushed ? "auto" : "auto",
              minHeight: pushed ? "300px" : "auto",
            }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
          >
            {/* 时间轴线条 */}
            <motion.div
              className="absolute w-full h-full"
              initial={{ x: "0%" }}
              animate={{
                x: pushed ? "95%" : "0%",
                left: pushed ? "auto" : "0",
                right: pushed ? "0" : "auto",
              }}
              transition={{ duration: 0.7, ease: "easeInOut" }}
            >
              {isDarkMode ? (
                <div className="timeline-animated-border-dark"></div>
              ) : (
                <div className="timeline-animated-border"></div>
              )}
            </motion.div>

            {/* 教育经历 */}
            <motion.div
              className="absolute w-full"
              initial={{ x: "-100%" }}
              animate={{ x: pushed ? "0%" : "-100%" }}
              transition={{ duration: 0.7, ease: "easeInOut" }}
            >
              <div className="relative pr-16">
                {educations.map((edu, index) => (
                  <motion.div
                    key={edu._id}
                    variants={fadeIn(scrollDirection, 0.1 + index * 0.1)}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: false }}
                    className="relative mb-12"
                  >
                    {isDarkMode ? (
                      <div className="absolute right-[-1.65rem] top-0 w-4 h-4 rounded-full timeline-dot-dark border-2 border-gray-600" />
                    ) : (
                      <div className="absolute right-[-1.65rem] top-0 w-4 h-4 rounded-full timeline-dot border-2 border-gray-600" />
                    )}
                    <div className="relative mr-8 bg-white/10 backdrop-blur-lg p-8 rounded-xl shadow-xl dark:hover:shadow-[0_10px_30px_rgba(59,246,81,0.4)]">
                      <h3 className="text-3xl font-bold w-full flex justify-between">
                        {edu.title}
                        {edu.location && (
                          <div className="relative text-[20px] text-gray-800 dark:text-green-300 flex items-center gap-1">
                            <GrLocationPin className="inline-block" />
                            {edu.location}
                          </div>
                        )}
                      </h3>
                      <div className="text-xl mb-2 w-full flex justify-between">
                        {edu.school}
                        <p className="text-gray-800 dark:text-gray-300 mb-4">
                          {edu.date}
                        </p>
                      </div>

                      <ul className="text-[35px]">
                        {edu.relevantcourse.map((desc, i) => (
                          <li key={i} className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>{desc}</span>
                          </li>
                        ))}
                        <div className="absolute top-[7rem] right-8 flex flex-col items-center">
                          <p className="text-[20px] font-bold dark:text-green-600 text-rose-600 mb-1 leading-none">
                            GPA
                          </p>
                          <div className="border-2 dark:border-green-700 border-rose-600 rounded-lg overflow-hidden shadow-md">
                            {/* 上面的 GPA 数字 */}
                            <div className="text-[36px] font-bold px-4 py-2 dark:text-green-600 text-rose-600">
                              {edu.gpa?.split("/")?.[0]}
                            </div>
                            {/* 底部分数上限背景 */}
                            <div className="dark:bg-green-700 bg-rose-600 text-white text-[18px] px-2 w-full text-center">
                              /{edu.gpa?.split("/")?.[1] || "4.0"}
                            </div>
                          </div>
                        </div>
                      </ul>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* 工作经验 */}
            <motion.div
              className="w-full"
              initial={{ x: "0%" }}
              animate={{ x: pushed ? "100%" : "0%" }}
              transition={{ duration: 0.7, ease: "easeInOut" }}
            >
              <div className="relative pl-8">
                {experiences.map((exp, index) => (
                  <motion.div
                    key={exp._id}
                    variants={fadeIn(scrollDirection, 0.1 + index * 0.1)}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: false }}
                    className="relative mb-12"
                  >
                    {isDarkMode ? (
                      <div className="absolute left-[-1.75rem] top-0 w-4 h-4 rounded-full timeline-dot-dark border-2 border-gray-600" />
                    ) : (
                      <div className="absolute left-[-1.75rem] top-0 w-4 h-4 rounded-full timeline-dot border-2 border-gray-600" />
                    )}

                    <div className="relative ml-8 bg-white/10 backdrop-blur-lg p-8 rounded-xl shadow-xl dark:hover:shadow-[0_10px_30px_rgba(59,246,81,0.4)]">
                      <h3 className="text-3xl font-bold w-full flex justify-between">
                        {exp.title}
                        {exp.location && (
                          <div className="relative text-[20px] text-gray-800 dark:text-green-300 flex items-center gap-1">
                            <GrLocationPin className="inline-block" />
                            {exp.location}
                          </div>
                        )}
                      </h3>
                      <div className="text-xl mb-2 w-full flex justify-between">
                        {exp.company}
                        <p className="text-gray-800 dark:text-gray-300 mb-4">
                          {exp.date}
                        </p>
                      </div>

                      <ul className="space-y-2 text-lg">
                        {exp.description.map((desc, i) => (
                          <li key={i} className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>{desc}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="flex flex-wrap gap-2 mt-4">
                        {exp.jobskills.map((skill, index) => (
                          <span
                            key={index}
                            className="border-rose-600 dark:border-green-800 border-2 text-rose-600 dark:text-white text-sm font-medium px-3 py-1 rounded-full"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
